// Shared "Back" navigation for portal document views (EstimateView, InvoiceView).
//
// Root cause of the long-standing dead Estimate/Invoice back button (reported
// 2026-09-24, present since at least 2026-06-06 per git blame): every "Back"
// handler on these pages assumed `window.history.back()` had somewhere to go.
// That assumption breaks in the two most common ways customers/staff actually
// land on these pages:
//   1. Opened via `window.open(url, '_blank')` (Dashboard's estimate links) —
//      the new tab's session history has exactly one entry, so back() is a
//      silent no-op and the tab just sits there looking "broken".
//   2. Opened via a direct emailed/texted link (send_invoice_to_customer,
//      generate_payment_link, etc.) — the browser tab has no prior in-app
//      history at all, same silent no-op.
// `smartBack()` makes the button always do something useful instead of
// silently relying on ambient browser history:
//   - If this window/tab was opened by another (window.opener), close it —
//     that IS "back" for a popped-open tab.
//   - Else if this tab's session history actually has a previous entry,
//     use it (normal same-tab navigation, e.g. Dashboard -> /invoice).
//   - Else (direct/email link, no opener, no history) send the user to an
//     explicit, known-good destination instead of doing nothing.
export function smartBack(fallbackUrl = '/dashboard') {
  try {
    if (window.opener && !window.opener.closed) {
      window.close();
      // Some browsers silently refuse to close a tab that wasn't opened by
      // script (or the user has multiple tabs open under one opener) — if
      // we're still here after close() returns, fall through to the
      // explicit destination rather than leaving the button inert.
      setTimeout(() => {
        window.location.href = fallbackUrl;
      }, 250);
      return;
    }
  } catch {
    // window.opener can throw under strict cross-origin setups — ignore and
    // fall through to the history/fallback path below.
  }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  window.location.href = fallbackUrl;
}
